import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  Tooltip,
} from "recharts";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { ratingTier10, formatRating10 } from "../../totw/totwUtils";
import { FORM_RESULT_COLORS } from "../../competitions/shared/FormChips";
import {
  noratings,
  noratingsbg,
  noratingsicon,
} from "../../../constants/images";

export const AverageRatingChart = ({ chartData, isLoading = false }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const PAGE_SIZE = 5;
  // Ensure we revert to the last page (most recent matches) by default if valid
  const dataPoints = Array.isArray(chartData?.dataPoints)
    ? chartData.dataPoints
    : [];
  const totalPoints = dataPoints.length;
  const totalPages = totalPoints ? Math.ceil(totalPoints / PAGE_SIZE) : 0;

  // Default to the last page (most recent data)
  const [pageIndex, setPageIndex] = useState(
    totalPages > 0 ? totalPages - 1 : 0
  );
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipIconRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Calculate tooltip position based on icon position
  useEffect(() => {
    if (showTooltip && tooltipIconRef.current) {
      const rect = tooltipIconRef.current.getBoundingClientRect();
      // Position tooltip above the icon with some spacing
      setTooltipPosition({
        top: rect.top - 8, // Position above the icon
        left: rect.left + rect.width / 2, // Center horizontally
      });
    }
  }, [showTooltip]);

  useEffect(() => {
    // Reset to last page on data change
    if (totalPages > 0) {
      setPageIndex(totalPages - 1);
    }
  }, [totalPages]);

  const paginatedPoints = useMemo(() => {
    if (!totalPoints) {
      return [];
    }
    const start = pageIndex * PAGE_SIZE;
    return dataPoints.slice(start, start + PAGE_SIZE);
  }, [dataPoints, pageIndex, totalPoints]);

  const hasPagination =
    !isLoading &&
    !chartData.isLoading &&
    !chartData.noData &&
    totalPoints > PAGE_SIZE;

  // Pagination handlers
  const handlePrevPage = () => {
    if (pageIndex > 0) {
      setPageIndex(pageIndex - 1);
      setHoveredIndex(null);
    }
  };

  const handleNextPage = () => {
    if (pageIndex < totalPages - 1) {
      setPageIndex(pageIndex + 1);
      setHoveredIndex(null);
    }
  };

  // Calculate current range for display
  const currentStart = pageIndex * PAGE_SIZE + 1;
  const currentEnd = Math.min((pageIndex + 1) * PAGE_SIZE, totalPoints);

  // Determine active point (hovered or last in the list)
  const displayIndex =
    hoveredIndex !== null ? hoveredIndex : paginatedPoints.length - 1;

  // Compute average rating from available points (fallback to provided currentRating)
  const averageRating = useMemo(() => {
    const ratings = dataPoints
      .map((p) => {
        const n = Number(p?.rating);
        return Number.isFinite(n) ? n : null;
      })
      .filter((val) => val !== null);
    if (!ratings.length) return null;
    const sum = ratings.reduce((acc, val) => acc + val, 0);
    return sum / ratings.length;
  }, [dataPoints]);

  const formatRating = (value) => {
    if (!Number.isFinite(value)) return "-";
    return value.toFixed(2);
  };

  const displayRating = (() => {
    if (Number.isFinite(averageRating)) return formatRating(averageRating);
    const parsed = Number(chartData.currentRating);
    return formatRating(parsed);
  })();

  // Calculate Trend for the badge (Compare last point vs previous point, or simple average trend?)
  // User image shows "+0.2". Let's assume it compares the latest match rating vs the player's average or similiar.
  // For now, let's keep the logic of comparing the last point in the current view vs the first point in the current view,
  // OR simply the latest match rating - Average Rating?
  // Let's stick to a simple comparison: Latest Match Rating - Previous Match Rating (Last 2 points).
  const trendValue = useMemo(() => {
    if (dataPoints.length < 2) return "0.0";
    // Get the most recent two points
    const latest = dataPoints[dataPoints.length - 1]?.rating || 0;
    const prev = dataPoints[dataPoints.length - 2]?.rating || 0;
    return (latest - prev).toFixed(1);
  }, [dataPoints]);

  const isPositiveTrend = parseFloat(trendValue) >= 0;

  // Form Guide Data (Last 5 Matches)
  // We expect point.result to be 'W', 'D', 'L'. If missing, handled gracefully.
  // We take the LAST 5 points (most recent).
  const formGuideData = useMemo(() => {
    if (!dataPoints.length) return [];
    return dataPoints.slice(-5).reverse(); // Show most recent 5
  }, [dataPoints]);

  // Canonical W/D/L colors (UI-SPEC §1.3 via FormChips) — the old local
  // gradients had D red and L slate, both off-scheme AND semantically
  // swapped vs every other form guide on the platform.
  const getResultColor = (result) => FORM_RESULT_COLORS[result] || "#9CA3AF";

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-2 rounded text-xs shadow-lg">
          <p className="font-bold mb-1">{data.date}</p>
          <p>
            Rating:{" "}
            <span
              className="font-semibold"
              style={{ color: ratingTier10(data.rating).bg }}
            >
              {formatRating10(data.rating) ?? data.rating}
            </span>
          </p>
          {data.opponent && <p className="opacity-80">vs {data.opponent}</p>}
        </div>
      );
    }
    return null;
  };

  /**
   * Custom Dot Component
   * - Shows Green Badge on the active/hovered point
   * - Shows Filled Blue Circle on active point
   * - Shows Hollow Circle on other points
   */
  const CustomDot = (props) => {
    const { cx, cy, index, payload } = props;
    const isActive = index === displayIndex;
    // ONE color source for rating chips platform-wide (Marin 2026-08-20:
    // "Farbschema ... überall gleich"): ratingTier10, exactly like Matches/
    // History and TOTW. The old fixed green badge showed a 5.93 in green.
    const tier = ratingTier10(payload.rating);
    const label = formatRating10(payload.rating);

    if (isActive) {
      return (
        <g>
          {/* Rating badge above the point — tier-colored */}
          <rect
            x={cx - 16}
            y={cy - 40}
            width="32"
            height="22"
            rx="4"
            fill={tier.bg}
          />
          <text
            x={cx}
            y={cy - 24}
            textAnchor="middle"
            fill={tier.fg}
            fontSize="12"
            fontWeight="bold"
          >
            {label ?? payload.rating}
          </text>
          {/* Triangle Arrow for Badge */}
          <path
            d={`M${cx - 4} ${cy - 19} L${cx + 4} ${cy - 19} L${cx} ${
              cy - 14
            } Z`}
            fill={tier.bg}
          />

          {/* Active Dot with Glow Ring */}
          <circle cx={cx} cy={cy} r={8} fill="#BFDBFE" opacity="0.5" />
          <circle
            cx={cx}
            cy={cy}
            r={5}
            fill="#2183FE"
            stroke="white"
            strokeWidth={2}
          />
        </g>
      );
    }

    // Inactive point: hollow dot PLUS its rating as a small tier-colored
    // chip — Marin 2026-08-20: the rating is delivered for every point but
    // was only visible on the marked one.
    return (
      <g>
        {label != null && (
          <g>
            <rect
              x={cx - 13}
              y={cy - 32}
              width="26"
              height="16"
              rx="3"
              fill={tier.bg}
              opacity="0.92"
            />
            <text
              x={cx}
              y={cy - 20}
              textAnchor="middle"
              fill={tier.fg}
              fontSize="10"
              fontWeight="bold"
            >
              {label}
            </text>
          </g>
        )}
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill="white"
          stroke="#2183FE"
          strokeWidth={1.5}
        />
      </g>
    );
  };

  /**
   * Custom X-Axis Tick
   * - Renders Club Logo + Date
   * - Highlights active date in Blue
   */
  const CustomTick = ({ x, y, payload, index }) => {
    const isActive = index === displayIndex;
    const pointData = paginatedPoints[index];
    if (!pointData) return null;

    return (
      <g transform={`translate(${x},${y})`}>
        {/* Logo */}
        <foreignObject x={-12} y={-40} width={24} height={24}>
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100 overflow-hidden">
            <img
              src={pointData.countryImage || "/tp_small.png"}
              alt="logo"
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.src = "/tp_small.png";
              }}
            />
          </div>
        </foreignObject>

        {/* Date Text */}
        <text
          x={0}
          y={0}
          dy={10}
          textAnchor="middle"
          fill={isActive ? "#2183FE" : "#6B7280"}
          fontSize="12"
          fontWeight={isActive ? "700" : "400"}
        >
          {pointData.date}
        </text>
      </g>
    );
  };

  if (isLoading || chartData.isLoading) {
    return (
      <div className="h-64 bg-gray-50 rounded-xl animate-pulse w-full border border-gray-200"></div>
    );
  }

  if (chartData.noData || !paginatedPoints.length) {
    return (
      <div className="h-72 w-full relative overflow-hidden bg-white border border-[#D6DBEA] rounded-xl">
        <div className=" h-[80%] bottom-0 absolute w-full flex flex-col items-center justify-center gap-1">
          <img
            src={noratingsicon}
            className="absolute inset-0 left-1/2 transform -translate-x-1/2  z-10"
          />
          <img src={noratingsbg} className="absolute inset-0 w-full h-full" />

          <h1 className="relative z-20 text-center text-[#222A35] text-lg font-bold mt-10">No Statistics Available</h1>
          <p className="text-sm text-[#43526E] w-[30%] text-center">There are no statistics recorded for this player yet. Please check back later or try selecting a different season.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#D6DBEA] rounded-xl flex flex-col md:flex-row h-auto md:h-72 overflow-hidden mb-6">
      {/* Left Panel: Summary (~30%) */}
      <div className="w-full md:w-[20%] bg-[#2183FE14] p-6 flex flex-col border-b md:border-b-0 md:border-r border-[#D6DBEA] relative">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6 md:mb-12 mt-2">
          <h3 className="text-[#43526E] font-bold text-sm uppercase tracking-wide">
            AVERAGE RATING
          </h3>
          <div
            ref={tooltipIconRef}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="cursor-pointer"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="#6B7280"
              className="opacity-80"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
          </div>
          
          {/* Portal-based Tooltip */}
          {showTooltip &&
            createPortal(
              <div
                className="fixed z-[9999] pointer-events-none"
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                  transform: "translate(-50%, calc(-100% - 8px))",
                }}
              >
                <div className="bg-[#1A212F] text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg max-w-[200px] whitespace-normal">
                  This rating is the average of performance scores over the
                  selected period.
                </div>
                {/* Tooltip Arrow */}
                <div
                  className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-4 border-transparent border-t-[#1A212F]"
                />
              </div>,
              document.body
            )}
        </div>

        {/* Rating & Trend */}
        <div className="flex items-center gap-4 mb-auto">
          <span
            className="text-5xl font-extrabold tracking-tight"
            style={{
              color: Number.isFinite(averageRating)
                ? ratingTier10(averageRating).solid
                : "#43526E",
            }}
          >
            {displayRating}
          </span>

          <div
            className={`px-2 py-1 rounded-full text-sm font-bold flex items-center gap-1 ${
              isPositiveTrend
                ? "bg-[#DCFCE7] text-[#16A34A]"
                : "bg-red-50 text-red-600"
            }`}
          >
            {isPositiveTrend ? (
              <svg
                width="12"
                height="12"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 9L3.5 6.5L5.5 8.5L9 2M9 2H6M9 2V5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="transform rotate-90"
              >
                <path
                  d="M1 1L3.5 3.5L5.5 1.5L9 8M9 8H6M9 8V5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {isPositiveTrend ? "+" : ""}
            {trendValue}
          </div>
        </div>

        {/* Form Guide */}
        <div className="mt-8 md:mt-auto pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[#43526E] font-bold text-sm uppercase">
              FORM GUIDE
            </h4>
          </div>
          <div className="flex items-center gap-2">
            {formGuideData.map((point, i) => (
              <div
                key={point.id || i}
                title={`vs ${point.opponent} (${point.rating || '-'})`}
                style={{ background: getResultColor(point.result) }}
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm"
              >
                {point.result || "-"}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel: Chart (~70%) */}
      <div className="w-full md:w-[80%] bg-white p-6 relative flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-[#374151] font-semibold text-lg">
            {totalPoints > PAGE_SIZE
              ? `Last ${totalPoints} Matches Performance`
              : `Last ${totalPoints} Matches Performance`}
          </h4>
          
          {/* Pagination Controls */}
          {hasPagination && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handlePrevPage}
                disabled={pageIndex === 0}
                className={`p-2 rounded-lg border transition-colors ${
                  pageIndex === 0
                    ? "border-[#D6DBEA] text-[#9CA3AF] cursor-not-allowed"
                    : "border-[#D6DBEA] text-[#43526E] hover:bg-[#F3F4F6] cursor-pointer"
                }`}
                aria-label="Previous page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              
              <span className="text-sm text-[#6B7280] font-medium px-2 whitespace-nowrap">
                {currentStart}-{currentEnd} of {totalPoints}
              </span>
              
              <button
                onClick={handleNextPage}
                disabled={pageIndex >= totalPages - 1}
                className={`p-2 rounded-lg border transition-colors ${
                  pageIndex >= totalPages - 1
                    ? "border-[#D6DBEA] text-[#9CA3AF] cursor-not-allowed"
                    : "border-[#D6DBEA] text-[#43526E] hover:bg-[#F3F4F6] cursor-pointer"
                }`}
                aria-label="Next page"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className={`${isMobile ? 'h-[300px]' : 'flex-1 min-h-[180px]'}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={paginatedPoints}
              margin={{ top: 30, right: 30, left: 0, bottom: 20 }}
              onMouseMove={(state) => {
                if (state.isTooltipActive) {
                  setHoveredIndex(state.activeTooltipIndex);
                } else {
                  setHoveredIndex(null);
                }
              }}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <defs>
                <linearGradient id="colorRating" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2183FE" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#2183FE" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#E5E7EB"
              />

              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={<CustomTick />}
                padding={{ left: 20, right: 20 }}
              />

              <YAxis
                hide={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                domain={["dataMin - 1", "dataMax + 1"]}
                width={30}
              />

              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: "#2183FE",
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />

              <Area
                type="linear"
                dataKey="rating"
                stroke="#2183FE" // TP blue — matches the Value tab chart
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorRating)"
                dot={<CustomDot />}
                activeDot={false} // Handled by CustomDot via hoveredIndex logic if prefered, or re-enable activeDot
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
